import React, { useEffect, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes, faArrowUpFromBracket, faCirclePlus } from '@fortawesome/free-solid-svg-icons';
import styles from '../styles/pwa/InstallPrompt.module.scss';
import useStandalone from './useStandalone';

const DISMISS_KEY = 'conso_pwa_install_dismissed_v1';

function isIOS() {
  if (typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent || '';
  const iOSDevice = /iphone|ipad|ipod/i.test(ua);
  // iPadOS 13+ se prezintă ca Mac cu touch
  const iPadOS = navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1;
  return iOSDevice || iPadOS;
}

/*
 * Onboarding de instalare „Adaugă pe ecranul principal".
 *  - Android/desktop: prinde `beforeinstallprompt` și oferă buton nativ „Instalează".
 *  - iOS (fără eventul): afișează instrucțiuni Share → Adaugă pe ecranul principal.
 *  - Ascuns dacă e deja instalat (standalone) sau dacă userul a închis banner-ul.
 */
const InstallPrompt = () => {
  const standalone = useStandalone();
  const [deferred, setDeferred] = useState(null);
  const [visible, setVisible] = useState(false);
  const [platform, setPlatform] = useState(null); // 'android' | 'ios'

  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (standalone) return;

    let dismissed = false;
    try {
      dismissed = window.localStorage.getItem(DISMISS_KEY) === '1';
    } catch (e) {
      /* ignore */
    }
    if (dismissed) return;

    const onBIP = (e) => {
      e.preventDefault();
      setDeferred(e);
      setPlatform('android');
      setVisible(true);
    };
    window.addEventListener('beforeinstallprompt', onBIP);

    // iOS nu emite beforeinstallprompt → afișăm hint după o mică întârziere,
    // ca să nu apară brusc peste conținut la încărcare.
    let iosTimer = null;
    if (isIOS()) {
      iosTimer = window.setTimeout(() => {
        setPlatform('ios');
        setVisible(true);
      }, 2500);
    }

    const onInstalled = () => {
      setVisible(false);
      try {
        window.localStorage.setItem(DISMISS_KEY, '1');
      } catch (e) {
        /* ignore */
      }
    };
    window.addEventListener('appinstalled', onInstalled);

    return () => {
      window.removeEventListener('beforeinstallprompt', onBIP);
      window.removeEventListener('appinstalled', onInstalled);
      if (iosTimer) window.clearTimeout(iosTimer);
    };
  }, [standalone]);

  const dismiss = () => {
    setVisible(false);
    try {
      window.localStorage.setItem(DISMISS_KEY, '1');
    } catch (e) {
      /* ignore */
    }
  };

  const install = async () => {
    if (!deferred) return;
    deferred.prompt();
    try {
      await deferred.userChoice;
    } catch (e) {
      /* ignore */
    }
    setDeferred(null);
    setVisible(false);
  };

  if (standalone || !visible) return null;

  return (
    <div className={styles.root} role="dialog" aria-label="Instalează aplicația Conso">
      <button type="button" className={styles.close} onClick={dismiss} aria-label="Închide">
        <FontAwesomeIcon icon={faTimes} height="14" width="14" />
      </button>

      {platform === 'android' ? (
        <>
          <div className={styles.text}>
            <strong>Adaugă Conso pe ecran</strong>
            <span>Acces rapid la cont, RCA și convertor — ca o aplicație.</span>
          </div>
          <button type="button" className={styles.cta} onClick={install}>
            <FontAwesomeIcon icon={faCirclePlus} height="16" width="16" className="me-2" />
            Instalează
          </button>
        </>
      ) : (
        <div className={styles.text}>
          <strong>Adaugă Conso pe ecranul principal</strong>
          <span>
            Apasă <FontAwesomeIcon icon={faArrowUpFromBracket} height="13" width="13" /> (Share) în
            bara Safari, apoi „Adaugă pe ecranul principal".
          </span>
        </div>
      )}
    </div>
  );
};

export default InstallPrompt;
